import React from 'react';
import { 
  Brain, 
  Pill, 
  Heart, 
  Moon, 
  Utensils, 
  Activity,
  Clock 
} from 'lucide-react';
import { formatRelativeTime } from '@/utils/dateUtils';

interface HealthLog {
  id: string;
  log_type: string;
  severity?: 'low' | 'medium' | 'high';
  notes?: string;
  occurred_at: string;
  created_at: string;
}

interface RecentActivityWidgetProps {
  logs: HealthLog[];
  isLoading?: boolean;
}

const logTypeConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  seizure: {
    icon: <Brain className="w-4 h-4" />,
    color: 'text-red-500 bg-red-50',
    label: 'Seizure',
  },
  medication: {
    icon: <Pill className="w-4 h-4" />,
    color: 'text-blue-500 bg-blue-50',
    label: 'Medication',
  },
  symptom: {
    icon: <Heart className="w-4 h-4" />,
    color: 'text-terracotta bg-terracotta/10',
    label: 'Symptom',
  },
  sleep: {
    icon: <Moon className="w-4 h-4" />,
    color: 'text-purple-500 bg-purple-50',
    label: 'Sleep',
  },
  feeding: {
    icon: <Utensils className="w-4 h-4" />,
    color: 'text-green-500 bg-green-50',
    label: 'Feeding',
  },
  therapy: {
    icon: <Activity className="w-4 h-4" />,
    color: 'text-sage bg-sage/10',
    label: 'Therapy',
  },
};

function getSeverityBadge(severity?: string) {
  if (!severity) return null;
  
  const colors = {
    low: 'bg-green-100 text-green-700',
    medium: 'bg-yellow-100 text-yellow-700',
    high: 'bg-red-100 text-red-700',
  };
  
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${colors[severity as keyof typeof colors] || ''}`}>
      {severity}
    </span>
  );
}

export function RecentActivityWidget({
  logs,
  isLoading,
}: RecentActivityWidgetProps): React.ReactElement {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-3 animate-pulse">
            <div className="w-8 h-8 bg-gray-200 rounded-full" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-1" />
              <div className="h-3 bg-gray-200 rounded w-1/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-8">
        <Activity className="w-10 h-10 mx-auto mb-2 text-gray-300" />
        <p className="text-sm text-warm-gray">No recent activity</p>
        <p className="text-xs text-gray-400 mt-1">
          Start logging to see activity here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {logs.map((log) => {
        const config = logTypeConfig[log.log_type] || {
          icon: <Activity className="w-4 h-4" />,
          color: 'text-gray-500 bg-gray-50',
          label: log.log_type,
        };

        return (
          <div
            key={log.id}
            className="flex items-start gap-3 p-3 rounded-lg hover:bg-cream/50 transition-colors"
          >
            <div className={`p-2 rounded-full ${config.color}`}>
              {config.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-navy text-sm">
                  {config.label}
                </span>
                {getSeverityBadge(log.severity)}
              </div>
              {log.notes && (
                <p className="text-sm text-warm-gray truncate mt-0.5">
                  {log.notes}
                </p>
              )}
              <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                <Clock className="w-3 h-3" />
                {formatRelativeTime(log.occurred_at || log.created_at)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default RecentActivityWidget;
